import { memo } from "react";
import numeral from "numeral";

import { Badge, Card, Group, RingProgress, Stack, Text } from "@mantine/core";
import { TbTrophy } from "react-icons/tb";

import ObjectiveStatusBadge from "modules/strategy/components/ObjectiveStatusBadge";
import ObjectiveTypeChip from "modules/strategy/components/ObjectiveTypeChip";

import { formatDate } from "modules/core/utils/helpers";
import { strategyPrivateRoutesConstants } from "modules/strategy/routes/private/routesConstants";

import {
  ObjectiveStatus,
  ObjectiveType
} from "modules/strategy/types/entities";

export type DisplayObjectiveProps = {
  id: string;
  account_id: string;
  project_ids: string[];
  created_at: string;
  name: string;
  description?: string;
  type: ObjectiveType;
  status: ObjectiveStatus;
  start_date?: string;
  end_date?: string;
  outcome?: string;
  progress?: number;
  is_past_due: boolean;
};

const DisplayObjective = (props: DisplayObjectiveProps) => {
  const {
    id,
    name,
    description,
    type: objectiveType,
    status,
    start_date,
    end_date,
    progress,
    created_at
  } = props;

  return (
    <Card
      withBorder
      component="a"
      href={strategyPrivateRoutesConstants.single_objective.href(id)}
      target="_blank"
      rel="noopener noreferrer"
      shadow="xs"
      radius="lg"
    >
      <Stack gap="xs">
        <Badge
          size="xs"
          color="violet"
          variant="transparent"
          leftSection={<TbTrophy size={12} />}
          tt="capitalize"
        >
          Business Objective
        </Badge>

        <div>
          <Text size="sm" fw={600}>
            {name}
          </Text>

          {description ? (
            <Text size="xs" c="gray.6">
              {description}
            </Text>
          ) : null}
        </div>

        <Group gap="xs" justify="space-between">
          <Group gap="xs">
            <ObjectiveTypeChip
              withSuffix={false}
              objective_type={objectiveType}
              size="xs"
            />

            <ObjectiveStatusBadge isArchived={false} status={status} />

            {typeof progress === "number" &&
            objectiveType === "manual_metric" ? (
              <RingProgress
                sections={[
                  {
                    value: progress * 100,
                    color:
                      progress >= 0.75
                        ? "green"
                        : progress >= 0.5
                          ? "yellow"
                          : "red"
                  }
                ]}
                thickness={4}
                size={40}
                styles={{
                  label: {
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }
                }}
                label={
                  <Text
                    fw={600}
                    ta="center"
                    fz={9}
                    span
                    // w={12}
                    c={
                      progress >= 0.7
                        ? "green.7"
                        : progress >= 0.4
                          ? "yellow.7"
                          : "red.7"
                    }
                  >
                    {numeral(progress).format("0.[00]%")}
                  </Text>
                }
              />
            ) : null}
          </Group>

          {start_date && end_date ? (
            <Group gap="xs">
              <Text size="xs" c="gray.6">
                {formatDate({
                  datestring: start_date,
                  dateFormat: "dd MMM yyyy"
                })}
              </Text>

              <Text size="xs" c="gray.6">
                -
              </Text>

              <Text size="xs" c="gray.6">
                {formatDate({
                  datestring: end_date,
                  dateFormat: "dd MMM yyyy"
                })}
              </Text>
            </Group>
          ) : (
            <Text size="xs" c="gray.6">
              Created:{" "}
              {formatDate({
                datestring: created_at,
                dateFormat: "dd MMM yyyy"
              })}
            </Text>
          )}
        </Group>
      </Stack>
    </Card>
  );
};

export default memo(DisplayObjective);
